/**
 * Offers and coupons — `/v1/admin/offers/*`.
 *
 * **Nothing here decides whether a campaign is exhausted, or what a discount comes to.** The
 * server owns both: `exhausted` is written inside the transaction that takes the last redemption,
 * and the discount is resolved against a pinned price. A console that recomputed either would be
 * wrong and confident the first time two checkouts raced, and its number would disagree with the
 * booking the devotee actually paid for.
 *
 * **`held_paise` and `spent_paise` are kept apart all the way to the screen.** One is money
 * committed to bookings that may still die, the other is money gone. Summing them into a single
 * "used" figure would hide a campaign whose whole budget is held by carts nobody has paid for —
 * which reads as spent and is in fact recoverable.
 *
 * **The four-eyes second approver is a query parameter, not a body field**, matching the payable
 * release and the dispute resolution. The server refuses `second_approver_admin_id == self`, so
 * the console never has to decide that and never gets to be wrong about it.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type OfferRow = components['schemas']['OfferRow'];
export type OfferSpendRow = components['schemas']['OfferSpendRow'];
export type CouponRow = components['schemas']['CouponRow'];
export type OfferCreateIn = components['schemas']['OfferCreateIn'];
export type CouponCreateIn = components['schemas']['CouponCreateIn'];
export type OfferPatchIn = components['schemas']['OfferPatchIn'];
export type OfferScopeOption = components['schemas']['OfferScopeOption'];
export type OfferScopeOptions = components['schemas']['OfferScopeOptions'];
export type OfferAbuseReport = components['schemas']['OfferAbuseReport'];
export type OfferRedemptionRow = components['schemas']['OfferRedemptionRow'];
type OfferRedemptionList = components['schemas']['OfferRedemptionListResponse'];
type OfferList = components['schemas']['OfferListResponse'];
type CouponList = components['schemas']['CouponListResponse'];

export interface OfferQuery extends Record<string, unknown> {
  page: number;
  page_size: number;
  status?: string;
}

function search(params: OfferQuery): string {
  const query = new URLSearchParams();
  query.set('page', String(params.page));
  query.set('page_size', String(params.page_size));
  if (params.status) query.set('status', params.status);
  return query.toString();
}

export function useOffers(params: OfferQuery) {
  return useQuery({
    queryKey: keys.offers.list(params),
    queryFn: () => authedClient.request<OfferList>(`/v1/admin/offers?${search(params)}`),
  });
}

export function useOfferSpend(offerId: number) {
  return useQuery({
    queryKey: keys.offers.spend(offerId),
    queryFn: () => authedClient.request<OfferSpendRow>(`/v1/admin/offers/${offerId}/spend`),
  });
}

export function useOfferCoupons(offerId: number, page = 1) {
  return useQuery({
    queryKey: keys.offers.coupons(offerId, page),
    queryFn: () =>
      authedClient.request<CouponList>(`/v1/admin/offers/${offerId}/coupons?page=${page}`),
  });
}

/** The cities and pujas an offer's scope may name (admin doc 25 §3.2 item 3), served on the offers
 * router because growth cannot read the geo or catalogue consoles. Rarely changes. */
export function useOfferScopeOptions() {
  return useQuery({
    queryKey: keys.offers.scopeOptions(),
    queryFn: () => authedClient.request<OfferScopeOptions>('/v1/admin/offers/scope-options'),
    staleTime: 5 * 60_000,
  });
}

export function useCreateOffer() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: OfferCreateIn & { second_approver_admin_id?: number }) => {
      const { second_approver_admin_id, ...body } = input;
      const suffix =
        second_approver_admin_id === undefined
          ? ''
          : `?second_approver_admin_id=${second_approver_admin_id}`;
      return authedClient.request<OfferRow>(`/v1/admin/offers${suffix}`, {
        method: 'POST',
        body,
      });
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.offers.all() }),
  });
}

export function useSetOfferStatus() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { offerId: number; status: string; reason: string }) =>
      authedClient.request<OfferRow>(`/v1/admin/offers/${input.offerId}/status`, {
        method: 'POST',
        body: { status: input.status, reason: input.reason },
      }),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.offers.all() }),
  });
}

export function useMintCoupon(offerId: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CouponCreateIn) =>
      authedClient.request<CouponRow>(`/v1/admin/offers/${offerId}/coupons`, {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.offers.coupons(offerId) }),
  });
}

/** Edit a campaign. The server freezes the economic terms at the first redemption and says so
    with `offer_terms_frozen`; the console shows that refusal rather than predicting it. */
export function useUpdateOffer() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { offerId: number; patch: OfferPatchIn; second_approver_admin_id?: number }) =>
      authedClient.request<OfferRow>(
        `/v1/admin/offers/${input.offerId}${
          input.second_approver_admin_id === undefined
            ? ''
            : `?second_approver_admin_id=${input.second_approver_admin_id}`
        }`,
        { method: 'PATCH', body: input.patch },
      ),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.offers.all() }),
  });
}

/** The farming lens (admin doc 25 §3.3). Read-only: it names patterns, it rules on nothing. */
export function useOfferAbuseReport(days: number) {
  return useQuery({
    queryKey: keys.offers.abuse(days),
    queryFn: () =>
      authedClient.request<OfferAbuseReport>(`/v1/admin/offers/abuse-report?days=${days}`),
  });
}

/** The redemption ledger behind `/spend` (admin doc 25 §3.2 item 7), paged on the server. */
export function useOfferRedemptions(offerId: number, params: OfferQuery) {
  return useQuery({
    queryKey: keys.offers.redemptions(offerId, params),
    queryFn: () =>
      authedClient.request<OfferRedemptionList>(
        `/v1/admin/offers/${offerId}/redemptions?${search(params)}`,
      ),
  });
}
